/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IStoreInfo } from '@msdyn365-commerce-modules/bopis-utilities';
import {
    getConfigureErrors,
    IBuyboxAddToCartViewProps,
    IBuyboxCallbacks,
    IBuyboxCommonResources,
    // IBuyboxData,
    IBuyboxResources as IBuyboxExtentionResources,
    // IBuyboxFindInStoreViewProps,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductConfigureDropdownViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxProductQuantityViewProps,
    IBuyboxShopSimilarLookViewProps,
    IBuyboxState,
    IBuyboxViewProps
} from '@msdyn365-commerce-modules/buybox';
import {
    ArrayExtensions,
    getFullAvailableInventoryNearby,
    GetFullAvailableInventoryNearbyInput,
    IFullOrgUnitAvailability
} from '@msdyn365-commerce-modules/retail-actions';
import { IncrementalQuantity, INodeProps, ITelemetryContent, Module, Node } from '@msdyn365-commerce-modules/utilities';
import { AddToWishlistComponent, IWishlistActionErrorResult, PriceComponent } from '@msdyn365-commerce/components';
import {
    CartLine,
    format,
    ItemAvailability,
    PriceLookupContext,
    ProductPrice,
    ProjectionDomain,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy';
import { reaction } from 'mobx';
import * as React from 'react';

import { getCartState } from '@msdyn365-commerce/global-state';
import { addCartLinesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import { getActivePricesAction, GetActivePricesInput } from '../../../actions/getactiveprices.action';
import { GetProductUnitOfMeasureInput, getProductUnitsOfMeasureAction } from '../../../actions/getproductunitsofmeasure.action';
import { getUOMDefinitionAsync } from '../../../custom-actions/DataActionExtension.g';
import { IGetUOMDefinitionResponse } from '../../../custom-actions/DataServiceEntities.g';
import { IBuyboxData } from '../../../dataTypes/buybox.data';
import { IBuyboxProps as IBuyboxExtentionProps, IBuyboxProps } from '../definition-extensions/buybox.ext.props.autogenerated';
import { renderAddToOrderTemplateButton } from './components/buybox-add-to-order-template';
import { getAttributeValuesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

/**
 * BuyBoxConstants enum.
 */
export enum BuyBoxConstants {
    zero = 0
}

/**
 * On Change function.
 * @param callbacks -Callbacks.
 * @returns Update quantity.
 */
const onChangeHandler = (callbacks: IBuyboxCallbacks) => (newValue: number): boolean => {
    if (callbacks.updateQuantity) {
        return callbacks.updateQuantity(newValue);
    }
    return true;
};

/**
 * RenderQuantity.
 * @param quantityComponent - QuantityComponent.
 * @param callbacks - Callbacks.
 * @param props - Props.
 * @param state - State.
 * @param extentionResources - ExtentionResources.
 * @param quantityLimitsMessages - QuantityLimitsMessages.
 * @param max - Max.
 * @param telemetryContent - TelemetryContent.
 * @param unitOfMeasure - Unit Of Measure.
 * @returns -- Returns.
 */
const renderQuantity = (
    counterRef: any,
    quantityComponent: IBuyboxProductQuantityViewProps,
    callbacks: IBuyboxCallbacks,
    props: IBuyboxProps<IBuyboxData>,
    state: IBuyboxState,
    extentionResources: IBuyboxExtentionResources,
    quantityLimitsMessages: React.ReactNode,

    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    min: number | undefined,
    max: number | undefined,
    applyDefaultOrderSettings?: boolean,
    telemetryContent?: ITelemetryContent,
    unitOfMeasure?: React.ReactNode,
    selectedUnit?: string,
    cwUnit?: string | null,
    conversionFactor?: number | null
): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const { ContainerProps, LabelContainerProps, heading, errors } = quantityComponent;

    const { resources } = props;

    const { quantity } = state;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>

            <IncrementalQuantity
                ref={counterRef}
                id='ms-buybox__product-quantity-input'
                min={min}
                max={max}
                applyDefaultOrderSettings={applyDefaultOrderSettings}
                currentCount={quantity}
                onChange={onChangeHandler(callbacks)}
                inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
                decrementButtonAriaLabel={resources.decrementButtonAriaLabel}
                incrementButtonAriaLabel={resources.incrementButtonAriaLabel}
                minQuantityText={extentionResources.minQuantityText}
                maxQuantityText={extentionResources.maxQuantityText}
                telemetryContent={telemetryContent}
                disabled={cwUnit && conversionFactor ? true : false}
            />
            {/* {unitOfMeasure} */}
            {selectedUnit}
            {quantityLimitsMessages}
        </Node>
    );
};

const renderCatcWeightQuantity = (
    props: IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>,
    physicalQuantity: number | undefined,
    catchWeightQuantity: number,
    setCatchWeightQuantity: (val: number) => void
) => {
    const values = props.data?.product?.result?.ExtensionProperties ?? [];
    const conversionFactorAttribute = values.find(attribute => attribute.Key === 'ConversionFactor')?.Value;
    const cwUnitAttribute = values.find(attribute => attribute.Key === 'CWUnit')?.Value;
    const conversionFactorValue = conversionFactorAttribute?.DecimalValue ?? 0;
    const cwUnitValue = cwUnitAttribute?.StringValue;

    if (!physicalQuantity || conversionFactorValue <= 0 || !cwUnitValue) {
        return <></>;
    }
    const maxQuantity = Math.floor(physicalQuantity / conversionFactorValue);

    return (
        <Node className=''>
            <div className='ms-buybox__quantity'>
                <label className='ms-buybox__product-quantity-label' htmlFor='ms-buybox__product-quantity-input'>
                    <div className='ms-buybox__product-quantity-label-heading'>Quantity</div>
                </label>
                <div className='quantity' id='ms-buybox__product-quantity-input'>
                    <button
                        title=''
                        className='decrement quantity__controls disabled'
                        aria-hidden='true'
                        aria-label='Press to decrease the quantity by 1'
                        color='secondary'
                        onClick={() => {
                            if (catchWeightQuantity === 1) {
                                return;
                            } else {
                                setCatchWeightQuantity(--catchWeightQuantity);
                            }
                        }}
                    >
                        <span className='fas fa-minus quantity__controls-glyph' />
                    </button>
                    <input
                        type='number'
                        className='quantity-input'
                        pattern='[0-9]*'
                        defaultValue={'1'}
                        value={catchWeightQuantity}
                        aria-live='polite'
                        aria-label='quantity input'
                        role='spinbutton'
                        aria-valuemin={1}
                        aria-valuenow={1}
                        min={1}
                        aria-valuemax={maxQuantity}
                        max={maxQuantity}
                        onChange={e => {
                            const rawValue = e.target.value;
                            if (rawValue === '') {
                                setCatchWeightQuantity(1);
                                return;
                            }
                            const value = parseInt(rawValue, 10);
                            if (isNaN(value)) {
                                setCatchWeightQuantity(1);
                                return;
                            }
                            const clampedValue = Math.min(Math.max(value, 1), maxQuantity);
                            setCatchWeightQuantity(clampedValue);
                        }}
                    />
                    <button
                        title='Press to increment quantity by 1'
                        className='increment quantity__controls '
                        aria-hidden='false'
                        aria-label='Press to increment quantity by 1'
                        color='secondary'
                        onClick={() => {
                            if (catchWeightQuantity < maxQuantity) {
                                setCatchWeightQuantity(++catchWeightQuantity);
                            } else {
                                console.log('Max Quantity reached');
                                return;
                            }
                        }}
                    >
                        <span className='fas fa-plus quantity__controls-glyph' />
                    </button>
                </div>
                {cwUnitValue}
            </div>
        </Node>
    );
};

/**
 * Render key in price function.
 * @param keyInPrice - KeyInPrice.
 * @returns -- Returns.
 */
const renderKeyInPrice = (keyInPrice: IBuyboxKeyInPriceViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input } = keyInPrice;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {input}
        </Node>
    );
};

/**
 * RenderAddToCart.
 * @param addToCart - AddToCart.
 * @returns -- Returns.
 */
const renderAddToCart = (
    availabilityInStore: boolean,
    addToCart: IBuyboxAddToCartViewProps,
    isAuthenticated: boolean,
    props: IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>,
    selectedUnit: string,
    state: IBuyboxState,
    cwUnitValue: string,
    ConversionFactorValue: number | null,
    catchWeightQuantity: number,
    unitOfMeasureData: boolean
): JSX.Element => {
    const { ContainerProps, errorBlock } = addToCart;
    let isProductOutOfStock = !availabilityInStore;
    // if (React.isValidElement(button)) {
    //     isProductOutOfStock =  button?.props?.productAvailability?.AvailableQuantity === 0 ? true : false;
    //     // isProductOutOfStock = !availabilityInStore;
    // }

    // const disabledStyles = { cursor: 'not-allowed !important' };
    // const buttonCloneEl =
    //     React.isValidElement(button) && React.cloneElement(button as JSX.Element, { disabled: true, style: disabledStyles });
    const handleClickAddTocart = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        addToCartCustom(props, selectedUnit, state, cwUnitValue, ConversionFactorValue, catchWeightQuantity);
    };
    // addToCart.button
    const [displayAddToCartButton, setDisplayAddtoCartButton] = React.useState(false);
    React.useEffect(() => {
        if (isAuthenticated && props.data.getPaymentState?.result?.isStoreSelected && !isProductOutOfStock && unitOfMeasureData) {
            setDisplayAddtoCartButton(true);
        } else {
            setDisplayAddtoCartButton(false);
        }
    }, [props.data?.getPaymentState?.result?.isStoreSelected, isAuthenticated, availabilityInStore]);
    return (
        <Node {...ContainerProps}>
            {errorBlock}
            {/* UnComment below code */}
            {/* {isAuthenticated ? button : buttonCloneEl} */}
            {
                <button
                    className='msc-add-to-cart '
                    aria-label={displayAddToCartButton ? 'Add to cart' : 'Please Select Preferred Store Locator'}
                    title={displayAddToCartButton ? 'Add to cart' : 'Please Select Preferred Store Locator'}
                    data-exp-event-id='Default product details.buybox.Add to cart.AddToCart'
                    onClick={handleClickAddTocart}
                    disabled={displayAddToCartButton ? false : true}
                    style={{ cursor: displayAddToCartButton ? 'pointer' : 'not-allowed' }}
                >
                    {isProductOutOfStock || !unitOfMeasureData ? 'Out of Stock' : 'Add to cart'}
                </button>
            }
        </Node>
    );
};

const addToCartCustom = async (
    props: IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>,
    selectedUnit: string,
    state: IBuyboxState,
    cwUnitValue: string,
    ConversionFactorValue: number | null,
    catchWeightQuantity: number
) => {
    const preferredStore = JSON.parse(localStorage.getItem('_msdyn365__preferredStore_') || '');
    const {
        data: {
            cart: { result },
            product
        },
        context: { actionContext }
    } = props;
    const cartState = await getCartState(actionContext);
    const cartId = result?.cart?.Id!;
    const addProductToCart: CartLine[] = [];
    if (product) {
        const resolvedProduct = await product;

        const createCartItem = () => ({
            CatalogId: props.context.request.apiSettings.catalogId,
            DeliveryMode: 'BOPIS',
            FulfillmentStoreId: preferredStore.StoreId,
            Quantity: ConversionFactorValue || state.quantity,
            ItemId: resolvedProduct.ItemId,
            ProductId: resolvedProduct.RecordId,
            UnitOfMeasureSymbol: cwUnitValue ? resolvedProduct.DefaultUnitOfMeasure : selectedUnit,
            WarehouseId: preferredStore.StoreId,
            IsGiftCardLine: resolvedProduct.IsGiftCard,
            Price: resolvedProduct.Price,
            EntryMethodTypeValue: resolvedProduct.ItemServiceTypeValue,
            TrackingId: ''
        });

        if (resolvedProduct.DefaultUnitOfMeasure === 'lb') {
            for (let i = 0; i < catchWeightQuantity; i++) {
                addProductToCart.push(createCartItem());
            }
        } else {
            addProductToCart.push(createCartItem());
        }
    }

    await addCartLinesAsync({ callerContext: actionContext }, cartId, addProductToCart).then(async newCart => {
        await cartState.updateCart({ newCartObject: newCart });
    });
};

/**
 * RenderFindInStore.
 * @param findInStore - FindInStore.
 * @returns -- Returns.
 */
// const renderFindInStore = (findInStore: IBuyboxFindInStoreViewProps, isAuthenticated: boolean): JSX.Element => {
//     // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
//     const { ContainerProps, storeSelector, heading, description, errors, button, modal, productPickupOptionList } = findInStore;
//     const disabledButtonClass = 'disabled-button';
//     const disabledStyles = { cursor: 'pointer' };
//     const buttonCloneFind =
//         React.isValidElement(button) &&
//         React.cloneElement(button as JSX.Element, {
//             disabled: true,
//             style: { ...disabledStyles },
//             className: `${button.props.className} ${disabledButtonClass}`
//         });
//     return (
//         <Node {...ContainerProps}>
//             {storeSelector}
//             {heading}
//             {productPickupOptionList}
//             {description}
//             {errors}
//             {isAuthenticated ? button : buttonCloneFind}
//             {modal}
//         </Node>
//     );
// };

/**
 * RenderAddToOrderTemplate.
 * @param props - Buybox view props.
 * @param state - Buybox state.
 * @param callbacks - Buybox callbacks.
 * @returns -- Returns.
 */
const renderAddToOrderTemplate = (
    props: IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks,
    uom: string
): JSX.Element | null => {
    const product = props.data.product.result;
    if (!props.addToOrderTemplate || !product) {
        return null;
    }

    const orderTemplateButton = renderAddToOrderTemplateButton(props, state, callbacks, { ...product, DefaultUnitOfMeasure: uom });

    return (
        <Node {...props.addToOrderTemplate.ContainerProps}>
            {props.addToOrderTemplate.errorBlock}
            {orderTemplateButton}
        </Node>
    );
};

/**
 * Add to wishlist failed function.
 * @param callbacks -Buybox callbacks.
 * @param resources -Buybox resources.
 * @param product -Simple product.
 * @returns Update error state.
 */
const onAddToWishlistFailed = (callbacks: IBuyboxCallbacks, resources: IBuyboxCommonResources, product: SimpleProduct | undefined) => (
    result: IWishlistActionErrorResult
) => {
    callbacks.updateErrorState({
        errorHost: 'WISHLIST',
        configureErrors:
            result.status === 'MISSINGDIMENSION' ? getConfigureErrors(result.missingDimensions, resources, product?.IsGiftCard) : {}
    });
};

/**
 * RenderAddToWishlist.
 * @param props - Buybox view props.
 * @param state - Buybox state.
 * @param callbacks - Buybox callbacks.
 * @param product - Product data.
 * @returns -- Returns.
 */
const renderAddtoWishlistButton = (
    props: IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks,
    product: SimpleProduct
): React.ReactNode => {
    const { context, resources } = props;
    const wishlists = props.data.wishlists.result;
    const isShowWishlitButtonText = true;
    return (
        <AddToWishlistComponent
            className='msc-add-to-cart-extra-actions'
            addToWishlistButtonText={resources.addToWishlistButtonText}
            removeFromWishlistButtonText={resources.removeFromWishlistButtonText}
            addToWishlistMessage={resources.addToWishlistMessage}
            removedFromWishlistMessage={resources.removedFromWishlistMessage}
            addItemToWishlistError={resources.addItemToWishlistError}
            removeItemFromWishlistError={resources.removeItemFromWishlistError}
            nameOfWishlist={resources.nameOfWishlist}
            data={{ product, wishlists }}
            context={context}
            ariaRole='button'
            id={props.id}
            typeName={props.typeName}
            onError={onAddToWishlistFailed(callbacks, resources, product)}
            getSelectedProduct={state.selectedProduct}
            showButtonText={isShowWishlitButtonText}
            showButtonTooltip={false}
        />
    );
};

/**
 * RenderAddToWishlist.
 * @param props - Buybox viewprops.
 * @param state - Buybox state.
 * @param callbacks - Buybox callbacks.
 * @returns -- Returns.
 */
const renderAddToWishlist = (
    props: IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks
): JSX.Element | null => {
    const product = props.data.product.result;
    if (!props.addToWishlist || !product) {
        return null;
    }

    if (!props.config.enableWishlist) {
        return null;
    }

    const wishlistButton = renderAddtoWishlistButton(props, state, callbacks, product);
    return (
        <Node {...props.addToWishlist.ContainerProps}>
            {props.addToWishlist.errorBlock}
            {wishlistButton}
        </Node>
    );
};

/**
 * RRenderShopSimilarItem.
 * @param shopSimilarItem - ShopSimilarItem.
 * @returns -- Returns.
 */
const renderShopSimilarItem = (shopSimilarItem: IBuyboxShopSimilarLookViewProps): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const { ContainerProps, errors, input } = shopSimilarItem;

    return (
        <Node {...ContainerProps}>
            {errors}
            {input}
        </Node>
    );
};

/**
 * RenderSocialShare.
 * @param socialShare - Social share react node array.
 * @returns -- Returns.
 */
const _renderSocialShare = (socialShare: React.ReactNode[]): JSX.Element | undefined => {
    if (!socialShare || socialShare.length === 0) {
        return undefined;
    }

    return <>{socialShare[0]}</>;
};

/**
 * RenderConfigureDropdown.
 * @param dropdown - Buybox product configure dropdown view props.
 * @returns -- Returns JSX.Element.
 */
const renderConfigureDropdown = (dropdown: IBuyboxProductConfigureDropdownViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors, select } = dropdown;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {select}
            {errors}
        </Node>
    );
};

/**
 * RenderConfigure.
 * @param configure - Buybox product configure view props.
 * @returns -- Returns JSX.Element.
 */
const renderConfigure = (configure: IBuyboxProductConfigureViewProps): JSX.Element => {
    const { ContainerProps, dropdowns } = configure;

    return <Node {...ContainerProps}>{dropdowns.map(renderConfigureDropdown)}</Node>;
};

/**
 * BuyboxView.
 * @param props - Props.
 * @returns -- Returns.
 */

const BuyboxView: React.FC<IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>> = props => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const {
        ModuleProps,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
        MediaGalleryContainerProps,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
        ProductInfoContainerProps,
        addToCart,
        addToOrderTemplate,
        addToWishlist,
        productComparisonButton,
        configure,
        description,
        findInStore,
        quantity,
        // price,
        title,
        rating,
        shopSimilarLook,
        keyInPrice,
        shopSimilarDescription,
        min,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
        max,
        applyDefaultOrderSettings,
        unitOfMeasure,
        callbacks,
        state,
        resources,
        quantityLimitsMessages,
        telemetryContent,
        app: { config: appConfig },
        catalogs
    } = props;
    const itemId = props.data.product.result?.ItemId;
    let skuText = itemId ? `${props.resources.skuText} ${itemId}` : props.resources.skuText;
    const preferredStoreInfo: IStoreInfo | undefined = props.data.storeSelectorStateManager.result?.preferredStore;
    const preferredStoreId = preferredStoreInfo?.StoreId ? preferredStoreInfo.StoreId : '';
    const preferredStoreName = preferredStoreInfo?.StoreName ? preferredStoreInfo.StoreName : '';

    const counterRef = React.useRef<{ inputRef: React.RefObject<HTMLInputElement> } | null>(null);

    const [availability, setAvailability] = React.useState<string>('');
    const [availabilityInStore, setAvailabilityInStore] = React.useState<boolean>(false);
    const [isStoreChange, setIsStoreChange] = React.useState<boolean>(false);

    const [unitOfMeasureData, setUnitOfMeasureData] = React.useState<
        { unitOfMeasureDropDownName: string; unitOfMeasureDropDownValue: string }[] | undefined
    >([]);
    const [selectedUnit, setSelectedUnit] = React.useState<string>('');
    const [newUpdatedPrice, setNewUpdatedPrice] = React.useState<ProductPrice>({});
    const [unitConversionText, setUnitConversionText] = React.useState('');
    const [showUnitOfMeasureData] = React.useState<boolean>(getAttributeValue('DualUOMEnabled') as boolean);
    const [cwUnitValue] = React.useState<string>(getAttributeValue('CWUnit') as string);
    const [ConversionFactorValue] = React.useState<number | null>(getAttributeValue('ConversionFactor') as number | null);
    const [physicalQuantity, setPhysicalQuantity] = React.useState<number | undefined>(max);
    const [catchWeightQuantity, setCatchWeightQuantity] = React.useState<number>(1);
    const [pricePerlb, setPricePerlb] = React.useState<number | string>('');
    const [brand, setBrand] = React.useState<string>('');
    const [packSize] = React.useState<string | null>(getAttributeValue('PackSize') as string | null);
    const [packDescription] = React.useState<string | null>(getAttributeValue('SizeDescription') as string | null);

    function getAttributeValue(key: string): boolean | string | number | null {
        const values = props.data?.product?.result?.ExtensionProperties ?? [];

        const attribute = values.find(attribute => attribute.Key === key)?.Value;

        if (!attribute) {
            return false;
        }

        const valueMap: Record<string, boolean | string | number | null> = {
            DualUOMEnabled: attribute.BooleanValue ?? false,
            CWUnit: attribute.StringValue ?? '',
            ConversionFactor: attribute.DecimalValue ?? null,
            PackSize: attribute.StringValue ?? '',
            SizeDescription: attribute.StringValue ?? ''
        };

        return valueMap[key] ?? false;
    }

    React.useEffect(() => {
        const { quantity } = state;
        const interval = setInterval(() => {
            if (counterRef.current) {
                const currentValue = counterRef?.current?.inputRef?.current?.value;
                if (quantity && currentValue !== quantity.toString()) {
                    if (currentValue === '') {
                        callbacks.updateQuantity?.(1);
                    }
                }
            }
        }, 300);

        return () => clearInterval(interval);
    }, [quantity]);

    const getProductUnitOfMeasureFunc = async () => {
        const getProductUnitOfMeasureInput = new GetProductUnitOfMeasureInput(props?.data?.product?.result?.RecordId, {
            IncludeQuantityLimitRules: true
        });
        const result = await getProductUnitsOfMeasureAction(getProductUnitOfMeasureInput, props.context.actionContext);
        if (result) {
            const preferredStore = JSON.parse(localStorage.getItem('_msdyn365__preferredStore_') || '');
            let filteredData: any = [];
            if (preferredStore && preferredStore.StoreId) {
                filteredData = result
                    ?.filter((item: any) => {
                        // Find UOMAvailableStores extension property
                        const uomStoreProp = item.ExtensionProperties?.find((prop: any) => prop.Key === 'UOMAvailableStores');

                        // If UOMAvailableStores does NOT exist → include item
                        if (!uomStoreProp?.Value?.StringValue) {
                            return true;
                        }

                        try {
                            // Parse StringValue
                            const stores = JSON.parse(uomStoreProp.Value.StringValue);

                            // If current storeId exists in PickupStore → exclude item
                            const isAvailableInStore = stores.some((store: any) => store.PickupStore === preferredStore.StoreId);

                            return !isAvailableInStore;
                        } catch (e) {
                            // If parsing fails, fail-safe → include item
                            return true;
                        }
                    })
                    .map((item: any) => ({
                        unitOfMeasureDropDownName: item.Description,
                        unitOfMeasureDropDownValue: item.Symbol
                    }));
            } else {
                filteredData = result
                    ?.filter((item: any) => item.Description === 'Case' || item.Description === 'Each') // Filter condition
                    .map((item: any) => ({
                        unitOfMeasureDropDownName: item.Description,
                        unitOfMeasureDropDownValue: item.Symbol
                    }));
            }

            setUnitOfMeasureData(prevState => {
                const newState = filteredData;
                return newState;
            });
            if (filteredData && filteredData.length) {
                setSelectedUnit(filteredData[0].unitOfMeasureDropDownValue);
            }
        }
    };

    const projectDomain: ProjectionDomain = {
        ChannelId: props.context.request.apiSettings.channelId,
        CatalogId: props.context.request.apiSettings.catalogId
    };

    const getActivePricesInputMeasure: PriceLookupContext | null = {
        HeaderContext: undefined,
        LineContexts: [
            {
                ProductRecordId: props?.data?.product?.result?.RecordId,
                UnitOfMeasureSymbol: selectedUnit
            }
        ]
    };

    const getActivePricesFunc = async () => {
        const getActivePricesInput = new GetActivePricesInput(
            projectDomain,
            undefined,
            undefined,
            undefined,
            undefined,
            true,
            false,
            false,
            getActivePricesInputMeasure
        );
        if (selectedUnit) {
            const result = await getActivePricesAction(getActivePricesInput, props.context.actionContext);
            if (result) {
                if (result[0]?.CustomerContextualPrice && ConversionFactorValue) {
                    setPricePerlb((result[0].CustomerContextualPrice / ConversionFactorValue)?.toFixed(2));
                }
                setNewUpdatedPrice(result[0]);
            }
        }
    };

    const getUOMConversion = async (selectedUnit: string) => {
        if (selectedUnit === 'Case') {
            const {
                context: { actionContext },
                data: { product }
            } = props;
            const itemId = product.result?.ItemId;
            if (itemId) {
                const result: IGetUOMDefinitionResponse = await getUOMDefinitionAsync(
                    { callerContext: actionContext },
                    itemId.toString(),
                    product.result?.RecordId || 0,
                    selectedUnit,
                    'ea'
                );
                if (result) {
                    setUnitConversionText(result.ConversionDefinition ? result.ConversionDefinition : '');
                }
            }
        } else {
            setUnitConversionText('');
        }
    };

    React.useEffect(() => {
        getProductUnitOfMeasureFunc();
    }, []);

    React.useEffect(() => {
        getActivePricesFunc();
        if (showUnitOfMeasureData) {
            getUOMConversion(selectedUnit);
        }
    }, [selectedUnit]);

    const getProductAtt = async () => {
        const {
            context: { actionContext }
        } = props;
        if (props?.data?.product?.result?.RecordId) {
            const productAtt = await getAttributeValuesAsync(
                { callerContext: actionContext },
                props?.data?.product?.result?.RecordId,
                props.context.request.apiSettings.channelId,
                props.context.request.apiSettings.catalogId
            );

            const brand = productAtt.find(item => item.Name === 'Brand');
            setBrand(brand?.TextValue ?? '');
        }
    };

    React.useEffect(() => {
        getProductAtt();
    }, []);

    /**
     * GetStockInventoryLabel.
     * @param availabilityWithHours - Stock availability with hours.
     * @returns -- Returns.
     */
    const getStockInventoryLabel = (availabilityWithHours: IFullOrgUnitAvailability | undefined): string | undefined => {
        if (availabilityWithHours && ArrayExtensions.hasElements(availabilityWithHours.ProductInventoryInformation)) {
            for (const productInventoryInformation of availabilityWithHours.ProductInventoryInformation) {
                if (
                    availabilityWithHours.OrgUnitAvailability?.OrgUnitLocation?.InventoryLocationId ===
                    productInventoryInformation.InventLocationId
                ) {
                    return productInventoryInformation.StockLevelLabel;
                }
            }
        }

        return undefined;
    };

    const getInventory = React.useCallback(async () => {
        const input = new GetFullAvailableInventoryNearbyInput(
            props.data.product.result?.RecordId,
            BuyBoxConstants.zero,
            BuyBoxConstants.zero,
            BuyBoxConstants.zero,
            BuyBoxConstants.zero,
            true
        );

        /**
         * IsProductInStock.
         * @param itemAvailabilities - Item availability array.
         * @returns -- Returns.
         */
        const isProductInStock = (itemAvailabilities: ItemAvailability[] | undefined): boolean => {
            if (!appConfig.enableStockCheck) {
                return true;
            }

            if (ArrayExtensions.hasElements(itemAvailabilities)) {
                if (itemAvailabilities[BuyBoxConstants.zero].AvailableQuantity!) {
                    setPhysicalQuantity(itemAvailabilities[BuyBoxConstants.zero].AvailableQuantity);
                }
                return itemAvailabilities[BuyBoxConstants.zero].AvailableQuantity! > BuyBoxConstants.zero;
            }

            return false;
        };

        await getFullAvailableInventoryNearby(input, props.context.actionContext)
            .catch((error: Error) => {
                props.telemetry.error(error.message);
            })
            .then(response => {
                if (response && ArrayExtensions.hasElements(response)) {
                    const isPreferredStore = (value: IFullOrgUnitAvailability) =>
                        value.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitNumber === preferredStoreId;
                    const filteredFullOrgUnitAvailability = response.filter(isPreferredStore);
                    const preferredStoreAvailability = ArrayExtensions.hasElements(filteredFullOrgUnitAvailability)
                        ? filteredFullOrgUnitAvailability[BuyBoxConstants.zero]
                        : undefined;
                    const stockStatusLabel: string | undefined = getStockInventoryLabel(preferredStoreAvailability);
                    const isInStock = isProductInStock(preferredStoreAvailability?.OrgUnitAvailability?.ItemAvailabilities);
                    const resourceLabel = isInStock ? props.resources.inStockText : props.resources.outOfStockText;
                    const stockText: string | undefined = stockStatusLabel ? stockStatusLabel : resourceLabel;
                    const preferredStoreAvailabilityText = `${format(
                        props.resources.availabilityAtPreferredStoreText,
                        preferredStoreName
                    )} ${unitOfMeasureData && unitOfMeasureData?.length > 0 ? stockText : props.resources.outOfStockText}`;
                    setAvailability(preferredStoreAvailabilityText);
                    setAvailabilityInStore(isInStock);
                }
            });

        setIsStoreChange(false);
    }, [
        props.data.product.result?.RecordId,
        appConfig.enableStockCheck,
        preferredStoreId,
        preferredStoreName,
        props.context.actionContext,
        props.resources.availabilityAtPreferredStoreText,
        props.resources.inStockText,
        props.resources.outOfStockText,
        props.telemetry
    ]);

    React.useEffect(() => {
        if (props.config.displayStockAvailability) {
            getInventory().catch((error: Error) => {
                props.telemetry.error(error.message);
            });
        }
    }, [getInventory, isStoreChange, props.telemetry, props.config.displayStockAvailability]);

    reaction(
        () => props.data.storeSelectorStateManager.result?.selectedStoreLocationId,
        () => {
            setIsStoreChange(true);
        }
    );

    const physicalAvailability = () => {
        const storeLocationObj = props.data?.product?.result?.ExtensionProperties?.find(item => item.Key === 'StoreLocation');
        const storeLocations = storeLocationObj?.Value?.StringValue ? JSON.parse(storeLocationObj?.Value?.StringValue) : [];

        if (!storeLocations || storeLocations.length === 0) {
            return null;
        }

        return (
            <>
                {storeLocations?.map((store: any) => (
                    <span key={store.RetailChannelId} className='availability_physical__location'>
                        <div className='availability__icon' />
                        <div className='wrapper'>
                            Physical location at {store.StoreName}:{' '}
                            <span className='highlight'>
                                <strong>{store.Location}</strong>
                            </span>
                        </div>
                    </span>
                ))}
            </>
        );
    };

    /**
     * RenderAvailabiltyAtPreferredStore.
     * @returns -- Returns.
     */
    const renderAvailabilityAtPreferredStore = (): JSX.Element | undefined => {
        const hasProductDimensions = ArrayExtensions.hasElements(props.data.product.result?.Dimensions);
        if (hasProductDimensions && props.data.product.result?.MasterProductId === undefined) {
            return undefined;
        }
        const containerProps: INodeProps = {
            className: 'ms-buybox__preferred-store-availability',
            tag: 'span'
        };

        return (
            <Node {...containerProps}>
                {availability}
                <div>{physicalAvailability()}</div>
            </Node>
        );
    };

    const user = props?.context?.request?.user;
    const handleDropdownChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedUnit(event.target.value);
    };

    const getCurrentCountValue = (): number | string | null => {
        const { quantity } = state;

        if (ConversionFactorValue && quantity) {
            return ConversionFactorValue * quantity;
        } else {
            return ConversionFactorValue;
        }
    };

    const getmaxQuantity = (): number | undefined => {
        return physicalQuantity ?? max;
    };

    const brandAndSizeInfoBox = () => {
        const unitOfMeasure = props.data?.product?.result?.DefaultUnitOfMeasure;
        return (
            <div>
                <div>
                    <span>
                        <strong>Brand: </strong> {brand}
                    </span>
                </div>

                <div>
                    <span>
                        <strong>Size: </strong> {packSize} / {packDescription} {unitOfMeasure === 'ea' ? 'each' : unitOfMeasure}
                    </span>
                </div>
            </div>
        );
    };

    return (
        <Module {...ModuleProps}>
            <Node {...MediaGalleryContainerProps}>{props.mediaGallery}</Node>
            <Node {...ProductInfoContainerProps}>
                {catalogs && catalogs[0].Name}
                {title}
                <Node className=''>
                    <Node className='msc-buybox__ratings-section'>
                        <div className='msc-buybox__ratings-section-sku-text'>{skuText} </div>
                        {rating}
                    </Node>
                    <div style={{ display: 'block', marginTop: '10px' }}>{brandAndSizeInfoBox()}</div>
                </Node>
                <Node className='msc-buybox__price-section'>
                    <div className='msc-buybox__price-section-text'>{props.resources.priceText}</div>

                    {user.isAuthenticated ? (
                        <>
                            <PriceComponent
                                id={'buybox'}
                                typeName={'buybox'}
                                context={props.context}
                                data={{ price: newUpdatedPrice }}
                                product={props.data.product?.result}
                                isPriceMinMaxEnabled={
                                    props.data.featureState.result?.find(
                                        featureState => featureState.Name === 'Dynamics.AX.Application.RetailSearchPriceRangeFeature'
                                    )?.IsEnabled
                                }
                                isSalesAgreementPriceFeatureEnabled={
                                    props.data.featureState.result?.find(
                                        featureState =>
                                            featureState.Name === 'Dynamics.AX.Application.RetailSalesAgreementPriceSupportFeature'
                                    )?.IsEnabled
                                }
                                priceResources={{
                                    priceRangeSeparator: resources.priceRangeSeparator,
                                    salesAgreementPricePrompt: resources.salesAgreementPricePrompt,
                                    salesAgreementExpirationDatePrompt: resources.salesAgreementExpirationDatePrompt,
                                    salesAgreementCommittedQuantityPrompt: resources.salesAgreementCommittedQuantityPrompt,
                                    salesAgreementRemainingQuantityPrompt: resources.salesAgreementRemainingQuantityPrompt
                                }}
                                freePriceText={resources.priceFree}
                                originalPriceText={resources.originalPriceText}
                                currentPriceText={resources.currentPriceText}
                            />

                            {cwUnitValue && ConversionFactorValue ? <div className='pricePerLb'>(${pricePerlb}/lb)</div> : null}
                        </>
                    ) : (
                        <a href={user?.signInUrl} className='login-register-link'>
                            {'sign in required'}
                        </a>
                    )}
                </Node>
                {user.isAuthenticated && configure && renderConfigure(configure)}
                {user.isAuthenticated && keyInPrice && renderKeyInPrice(keyInPrice)}

                <Node className='msc-buybox__bulk-purchase-section'>
                    <div className='msc-buybox__bulk-purchase-button-text'>
                        {/* <label htmlFor='unitOfMeasureDropdown'>Select Unit of Measure:</label> */}
                        <div className='ms-buybox__quantity'>
                            <div className='ms-buybox__uom-dropdown'>
                                {!cwUnitValue && !ConversionFactorValue && (
                                    <select
                                        id='unitOfMeasureDropdown'
                                        value={selectedUnit}
                                        onChange={handleDropdownChange}
                                        style={{ padding: '8px', margin: '8px 0', width: '65%', borderColor: '#c4c4c4' }}
                                    >
                                        {unitOfMeasureData?.map(unit => (
                                            <option key={unit.unitOfMeasureDropDownValue} value={unit.unitOfMeasureDropDownValue}>
                                                {unit.unitOfMeasureDropDownName}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>
                            {cwUnitValue && ConversionFactorValue && (
                                <>
                                    <div className='ms-buybox__uom-CW'>
                                        <div className='ms-buybox__uom-CW__wrapper'>
                                            <div>Catch Weight Unit</div>
                                            <div className='borderBox'>{cwUnitValue}</div>
                                        </div>
                                        <div className='ms-buybox__uom-CW__wrapper'>
                                            <div>Catch Weight lbs</div>
                                            <div className='borderBox'>{getCurrentCountValue()}</div>
                                        </div>
                                    </div>

                                    <div>
                                        {renderCatcWeightQuantity(props, physicalQuantity, catchWeightQuantity, setCatchWeightQuantity)}
                                    </div>
                                </>
                            )}
                            {!cwUnitValue &&
                                !ConversionFactorValue &&
                                showUnitOfMeasureData &&
                                unitConversionText &&
                                unitConversionText !== '' && (
                                    <div className='ms-buybox__uom-conversion-text'>Unit Conversion: {unitConversionText}</div>
                                )}
                            {!cwUnitValue &&
                                !ConversionFactorValue &&
                                quantity &&
                                renderQuantity(
                                    counterRef,
                                    quantity,
                                    callbacks,
                                    props,
                                    state,
                                    resources,
                                    quantityLimitsMessages,
                                    min,
                                    getmaxQuantity(),
                                    applyDefaultOrderSettings,
                                    telemetryContent,
                                    unitOfMeasure,
                                    selectedUnit,
                                    cwUnitValue,
                                    ConversionFactorValue
                                )}
                            {props.bulkPurchaseLink}
                        </div>
                    </div>
                </Node>
                {props.config.displayStockAvailability && preferredStoreId && renderAvailabilityAtPreferredStore()}
                <span>
                    {renderAddToCart(
                        availabilityInStore,
                        addToCart,
                        user.isAuthenticated,
                        props,
                        selectedUnit,
                        state,
                        cwUnitValue,
                        ConversionFactorValue,
                        catchWeightQuantity,
                        !!(unitOfMeasureData && unitOfMeasureData?.length)
                    )}
                </span>
                {/* {findInStore && renderFindInStore(findInStore, user.isAuthenticated)} */}
                {addToWishlist && renderAddToWishlist(props, state, callbacks)}
                {productComparisonButton}
                {addToOrderTemplate && renderAddToOrderTemplate(props, state, callbacks, selectedUnit)}
                {findInStore?.productPickupOptionList ? (
                    <div className='msc-buybox__pickup-options'>{findInStore.productPickupOptionList}</div>
                ) : null}
                {_renderSocialShare(props.slots && props.slots.socialShare)}
                <div className='msc-buybox__description-text'>{props.resources.shopText} </div>
                <Node className='msc-buybox__shop-description'>
                    {shopSimilarLook && renderShopSimilarItem(shopSimilarLook)}
                    {shopSimilarDescription && renderShopSimilarItem(shopSimilarDescription)}
                </Node>
                <Node className='msc-buybox__description-section'>
                    <div className='msc-buybox__description-section-text'>{props.resources.descriptionText}</div>
                    {description}
                </Node>
                {ArrayExtensions.hasElements(props.slots.productSpecification) ? (
                    <Node className='msc-buybox__product-specification'>{props.slots.productSpecification[0]}</Node>
                ) : null}
            </Node>
        </Module>
    );
};

export default BuyboxView;
